import { CircularProgress, Fade, Paper, Stack, Typography, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import bbox from '@turf/bbox'
import { FeatureCollection } from 'geojson'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LngLatBoundsLike, NavigationControl, useMap } from 'react-map-gl/dist/es5'

import {
  Bounds,
  FieldGeoJSONQueryOptions,
  FieldModel,
  array2dFromBounds,
  labelPositionForGeometry,
} from '@cibo/core'
import {
  FieldSearchBar,
  FieldSearchChips,
  PaginatedFieldsContext,
  useField,
  useLandManagement,
} from '@cibo/landmanager'
import {
  CDLLegend,
  FIELDS_LAYER_INTERACTIVE_IDS,
  FieldsLayer,
  LayerInteractionContext,
  LayerProvider,
  LayerRenderer,
  LayerSwitcher,
  MapGL,
  useFieldSearch,
} from '@cibo/mapgl'
import { MAINLAND_US_BOUNDS } from '@cibo/ui'

import { useAppStore } from 'store'
import { useConfirmOpportunityDialog } from '../useConfirmOpportunity'
import { FieldPopover } from './FieldPopover'

const LIMIT = 500

export const LandManagerMapView = () => (
  <LayerProvider>
    <MapGL
      initialViewport={{
        // TODO: replace initial bounds with the `bounds` concept in LandManagerMap below
        bounds: MAINLAND_US_BOUNDS,
      }}
      interactiveLayerIds={FIELDS_LAYER_INTERACTIVE_IDS}
    >
      <LandManagerMap />
      <NavigationControl showZoom position="bottom-right" />
    </MapGL>
  </LayerProvider>
)

const LandManagerMap = () => {
  const { t } = useTranslation('impact/pages/LandManagerMap')
  const { filters } = useContext(PaginatedFieldsContext)
  const { bounds, setBounds } = useAppStore()
  const { current } = useMap()
  const [resourceId, setResourceId] = useState<string>()
  const [query, setQuery] = useState<FieldGeoJSONQueryOptions>()
  const [initialBoundsSet, setInitialBoundsSet] = useState<boolean>(!!bounds)
  const { spacing } = useTheme()
  const [showPopup, setShowPopup] = useState<{ longitude: number; latitude: number } | undefined>()
  const { data: field, isLoading: fieldIsLoading, isFetched: fieldIsFetched } = useField(resourceId)

  const { data: fields, isFetching } = useFieldSearch(query, {
    enabled: !current?.isMoving(),
  })

  const [mapFeatures, setMapFeatures] = useState<FeatureCollection | undefined>(fields || undefined)
  const { setConfirmOpportunity, confirmOpportunityDialog } = useConfirmOpportunityDialog()
  const { openFieldMetaDialog } = useLandManagement()
  const { setClickedFeatures } = useContext(LayerInteractionContext)

  const flyToBounds = useMemo(
    () => () => {
      if (!fields || fields.features.length === 0 || !current) {
        return null
      }
      const [minLng, minLat, maxLng, maxLat] = bbox(fields)
      current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          pitch: 0,
          bearing: 0,
          padding: 120,
        }
      )
    },
    [fields, current]
  )

  useEffect(() => {
    if (bounds && current) {
      current.fitBounds(bounds as LngLatBoundsLike, {
        pitch: 0,
        bearing: 0,
        padding: 0,
      })
      setInitialBoundsSet(true)
    }
  }, [current])

  useEffect(() => {
    if (fields && !isFetching) {
      setMapFeatures(fields)
    }
  }, [isFetching, fields, setMapFeatures])

  useEffect(() => {
    if (!isFetching) {
      if (!initialBoundsSet) {
        flyToBounds()
        setInitialBoundsSet(true)
      }
    }
  }, [fields, current, isFetching, initialBoundsSet, bounds])

  useEffect(() => {
    if (!!current) {
      current.on('moveend', e => {
        setBounds(current.getBounds().toArray())
      })
    }
  }, [current])

  useEffect(() => {
    const nonBoundsQuery = {
      ...filters,
      limit: LIMIT,
      owner: filters.owner,
    }
    if (!!bounds) {
      setQuery({
        ...nonBoundsQuery,
        lowerLeft: bounds[0] as [number, number],
        upperRight: bounds[1] as [number, number],
      })
    } else {
      // @ts-ignore evidently works without bounds?
      setQuery(nonBoundsQuery)
    }
  }, [filters, bounds])

  const handleFieldClicked = useCallback(
    (resourceId?: string) => {
      const fieldFeature = fields?.features.find(f => f.properties.resourceId === resourceId)

      if (fieldFeature) {
        setResourceId(resourceId)

        const point = labelPositionForGeometry(fieldFeature.geometry)

        setShowPopup({
          // @ts-ignore it's a number
          latitude: point[1],
          // @ts-ignore it's a number
          longitude: point[0],
        })
      }
    },
    [isFetching]
  )

  const handleTypeaheadResult = useCallback(
    (field: FieldModel) => {
      setShowPopup(undefined)

      const feature = field.geometryDetail?.input.feature

      if (feature) {
        setMapFeatures({ type: 'FeatureCollection', features: [feature] })
      }

      current?.fitBounds(bbox(field.geometry) as LngLatBoundsLike, {
        padding: typeof spacing === 'function' ? parseInt(`${spacing(8)}`, 10) : 0,
        bearing: 0,
        pitch: 0,
      })
    },
    [current, setShowPopup, spacing]
  )
  const handleLocationResult = useCallback(
    (location: Bounds) => {
      setShowPopup(undefined)

      if (!location) return

      current?.fitBounds(array2dFromBounds(location) as LngLatBoundsLike, {
        padding: typeof spacing === 'function' ? parseInt(`${spacing(8)}`, 10) : 0,
        bearing: 0,
        pitch: 0,
      })
    },
    [current, setShowPopup, spacing]
  )

  return (
    <>
      {!!showPopup && (
        <FieldPopover
          field={field}
          fieldIsLoading={fieldIsLoading}
          onClickOpportunity={({ programId, field }) => setConfirmOpportunity({ programId, field })}
          onClickMeta={(resourceId: string) => openFieldMetaDialog({ resourceId })}
          showPopup={showPopup}
          onClose={() => {
            setClickedFeatures()
            setShowPopup(undefined)
          }}
          fieldIsFetched={fieldIsFetched}
        />
      )}
      <LayerRenderer county />
      <FieldsLayer
        featureCollection={mapFeatures}
        onFieldClicked={handleFieldClicked}
        zoomToField={false}
      />

      <Box
        padding={1}
        paddingBottom={4}
        position="relative"
        height="100%"
        sx={{ pointerEvents: 'none' }}
      >
        <Stack justifyContent="space-between" height="100%">
          <Stack direction="row" justifyContent="space-between">
            {/*  using a stack here breaks map interactions */}
            <Box sx={{ pointerEvents: 'all' }} display="flex" flex-direction="row">
              <FieldSearchBar
                onSelectTypeaheadResult={handleTypeaheadResult}
                onSelectLocationResult={handleLocationResult}
              />
              <FieldSearchChips hideBackground />
            </Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Fade in={isFetching}>
                <CircularProgress size={20} />
              </Fade>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            <Fade in={fields?.features.length === LIMIT}>
              <Box alignSelf="flex-start">
                <Paper elevation={0}>
                  <Box padding={0.5}>
                    <Typography variant="caption">{t('zoomIn', { limit: LIMIT })}</Typography>
                  </Box>
                </Paper>
              </Box>
            </Fade>

            <Box alignSelf="flex-start" sx={{ pointerEvents: 'all' }}>
              <Stack spacing={2} style={{ pointerEvents: 'all' }}>
                <CDLLegend />
                <LayerSwitcher />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {confirmOpportunityDialog}
    </>
  )
}
